import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { useIsPro } from "@/hooks/useIsPro";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { RefreshCw, Shield, Brain, Gauge, ChevronDown, ChevronUp, AlertTriangle, Activity } from "lucide-react";
import { ResponsiveContainer, RadialBarChart, RadialBar } from "recharts";

const stateColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };

// ═══ Regime Health Panel (per trade) ═══
function TradeRegimePanel({ tradeId, ticker, strategy, cachedData }) {
  const data = cachedData || null;

  if (!data) return (
    <div className="flex items-center justify-center py-6">
      <RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
      <span className="ml-2 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Analyzing {ticker} regime...</span>
    </div>
  );

  const h = data.health;
  const exit = data.exit_signal;
  const regime = data.regime;
  const alertColors = { GREEN: "#10B981", YELLOW: "#F59E0B", RED: "#EF4444" };
  const alertBg = { GREEN: "rgba(16,185,129,0.06)", YELLOW: "rgba(245,158,11,0.06)", RED: "rgba(239,68,68,0.06)" };
  const ac = alertColors[h?.alert_level] || "#71717A";

  const HEALTH_TOOLTIPS = {
    regime_continuity: "How stable the current regime has been. Low = regime changing frequently.",
    danger_distance: "How far the current state is from a danger/bearish transition. High = safer.",
    entropy_stability: "How certain the HMM is about the current regime state. High = confident.",
    pnl_path: "How the unrealised P&L trajectory compares to expected range. Low = deteriorating.",
  };

  const hasDrift = h?.entry_regime && regime?.state && h.entry_regime !== regime.state;
  const sojourn = h?.sojourn_days;
  const sojournLabel = sojourn != null ? (sojourn < 5 ? "Regime early — transition risk elevated" : sojourn <= 15 ? "Regime active" : "Regime mature — monitor for reversal") : null;

  return (
    <div className="p-3 space-y-3" style={{ background: alertBg[h?.alert_level] }} data-testid={`regime-health-${tradeId}`}>
      {/* Regime Drift Alert — top-level when drift detected */}
      {hasDrift && (
        <div className="border-l-4 px-3 py-2 rounded-sm" style={{ borderColor: "#F59E0B", background: "rgba(245,158,11,0.08)" }} data-testid={`regime-drift-${tradeId}`}>
          <div className="flex items-center gap-1.5 mb-0.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[10px] font-bold text-amber-500">Regime Drift Detected</span>
          </div>
          <div className="text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
            Entry: <span className="font-bold" style={{ color: stateColors[h?.entry_regime] || "var(--hoip-text-primary)" }}>{h?.entry_regime}</span>
            {" → "}Current: <span className="font-bold" style={{ color: stateColors[regime?.state] || "var(--hoip-text-primary)" }}>{regime?.state}</span>
            <span className="ml-2 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>Entry prob now: {(h?.entry_regime_prob_now * 100)?.toFixed(0)}%</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Health Score with tooltips */}
        <div className="border rounded-sm p-3" style={{ borderColor: `${ac}40`, background: "var(--hoip-bg)" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" style={{ color: ac }} />
              <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Trade Health</span>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-sm font-bold font-mono-data" style={{ color: ac, background: `${ac}20` }}>
              {h?.alert_level} ({h?.health_score})
            </span>
          </div>
          <div className="space-y-2">
            {h?.components && Object.entries(h.components).map(([key, val]) => (
              <div key={key}>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="w-32 truncate capitalize" style={{ color: "var(--hoip-text-tertiary)" }}>{key.replace(/_/g, " ")}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--hoip-card)" }}>
                    <div className="h-full rounded-full" style={{ width: `${val}%`, background: val >= 70 ? "#10B981" : val >= 45 ? "#F59E0B" : "#EF4444" }} />
                  </div>
                  <span className="font-mono-data w-8 text-right" style={{ color: "var(--hoip-text-tertiary)" }}>{val}</span>
                </div>
                {HEALTH_TOOLTIPS[key] && <div className="text-[8px] ml-[136px]" style={{ color: "var(--hoip-text-tertiary)", opacity: 0.7 }}>{HEALTH_TOOLTIPS[key]}</div>}
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] px-2 py-1 border rounded-sm" style={{ borderColor: `${ac}30`, color: "var(--hoip-text-secondary)" }}>
            {h?.recommended_action}
          </div>
        </div>

        {/* Current Regime + Drift details */}
        <div className="border rounded-sm p-3" style={{ borderColor: hasDrift ? "#F59E0B40" : "var(--hoip-border)", background: "var(--hoip-bg)" }}>
          <div className="flex items-center gap-1.5 mb-2">
            <Gauge className="w-3.5 h-3.5" style={{ color: stateColors[regime?.state] }} />
            <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Current Regime</span>
            {sojournLabel && <span className="text-[8px] ml-auto px-1.5 py-0.5 rounded-sm" style={{ background: sojourn < 5 ? "rgba(239,68,68,0.1)" : sojourn <= 15 ? "rgba(59,130,246,0.1)" : "rgba(245,158,11,0.1)", color: sojourn < 5 ? "#EF4444" : sojourn <= 15 ? "#3B82F6" : "#F59E0B" }}>{sojournLabel}</span>}
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold" style={{ color: stateColors[regime?.state], background: `${stateColors[regime?.state]}15` }}>
              {regime?.state} ({(regime?.prob * 100)?.toFixed(0)}%)
            </span>
          </div>
          <div className="space-y-1 text-[10px]">
            <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Entry Regime</span><span className="font-mono-data" style={{ color: hasDrift ? "#F59E0B" : "inherit" }}>{h?.entry_regime}{hasDrift ? " ≠" : ""}</span></div>
            <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Entry Prob Now</span><span className="font-mono-data" style={{ color: (h?.entry_regime_prob_now || 0) < 0.3 ? "#EF4444" : "inherit" }}>{(h?.entry_regime_prob_now * 100)?.toFixed(0)}%</span></div>
            <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Danger Prob</span><span className="font-mono-data" style={{ color: h?.danger_state_prob > 0.3 ? "#EF4444" : "inherit" }}>{(h?.danger_state_prob * 100)?.toFixed(0)}%</span></div>
            <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Entropy</span><span className="font-mono-data">{h?.entropy?.toFixed(2)}</span></div>
            {sojourn != null && <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Sojourn</span><span className="font-mono-data">{sojourn}d</span></div>}
          </div>

          {/* State probability bars */}
          {regime?.state_probs && (
            <div className="mt-2 space-y-0.5">
              {["Quiet Range", "Bullish Trend", "Bearish Trend", "Volatile"].map((label, i) => (
                <div key={label} className="flex items-center gap-1 text-[9px]">
                  <span className="w-3 h-3 rounded-full" style={{ background: stateColors[label], opacity: 0.5 }} />
                  <span className="w-16 truncate" style={{ color: "var(--hoip-text-tertiary)" }}>{label.split(" ")[0]}</span>
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--hoip-card)" }}>
                    <div className="h-full rounded-full" style={{ width: `${(regime.state_probs[i] || 0) * 100}%`, background: stateColors[label] }} />
                  </div>
                  <span className="font-mono-data w-8 text-right">{((regime.state_probs[i] || 0) * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Exit Signal */}
      {exit && (
        <div className="border rounded-sm p-3" style={{ borderColor: exit.exit_type === "HARD" ? "#EF4444" : "#F59E0B", background: exit.exit_type === "HARD" ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.06)" }} data-testid={`exit-signal-${tradeId}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: exit.exit_type === "HARD" ? "#EF4444" : "#F59E0B" }} />
              <span className="text-[10px] font-bold" style={{ color: exit.exit_type === "HARD" ? "#EF4444" : "#F59E0B" }}>
                {exit.exit_type} EXIT — {exit.urgency} urgency
              </span>
            </div>
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono-data font-bold" style={{ color: "#EF4444", background: "rgba(239,68,68,0.15)" }}>{exit.suggested_action}</span>
          </div>
          <p className="text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>{exit.trigger_reason}</p>
        </div>
      )}
    </div>
  );
}

export default function ReliabilityLab() {
  const { requirePro } = useIsPro();
  const [pars, setPars] = useState(null);
  const [positions, setPositions] = useState([]);
  const [sormData, setSormData] = useState(null);
  const [trades, setTrades] = useState([]);
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sormLoading, setSormLoading] = useState(false);
  const [tradeRegimes, setTradeRegimes] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [relRes, tradeRes] = await Promise.all([
          api.get("/reliability/portfolio"),
          api.get("/trade-log"),
        ]);
        setPars(relRes.data?.pars || {});
        setPositions(relRes.data?.positions || []);
        const allTrades = tradeRes.data?.trades || [];
        setTrades(allTrades);
        // Pre-fetch regime health for all trades (fire and forget)
        allTrades.forEach(t => {
          if (t.id) {
            api.get(`/regime/monitor/${t.id}`).then(({ data: rd }) => {
              setTradeRegimes(prev => ({ ...prev, [t.id]: rd }));
            }).catch(() => {});
          }
        });
      } catch (err) { console.error("Reliability error:", err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const loadSorm = async () => {
    if (!requirePro("/portfolio/sorm")) return;
    setSormLoading(true);
    try { const { data } = await api.get("/portfolio/sorm"); setSormData(data); }
    catch (e) { console.warn(e); }
    finally { setSormLoading(false); }
  };

  if (loading) return (<div className="flex items-center justify-center h-full p-8"><RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>);

  const sorm = sormData?.sorm || sormData?.form || {};
  const breachPct = ((sorm.pf_sorm ?? sorm.failure_probability ?? 0) * 100);
  const reliabilityPct = 100 - breachPct;
  const gaugeColor = reliabilityPct >= 90 ? "#10B981" : reliabilityPct >= 75 ? "#F59E0B" : "#EF4444";
  const sobol = sormData?.sobol?.indices || [];

  const alertColors = { GREEN: "#10B981", YELLOW: "#F59E0B", RED: "#EF4444" };

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6" data-testid="reliability-lab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Reliability Lab</h1>
          <span className="text-[9px] px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(59,130,246,0.1)", color: "#3B82F6" }}>Regime + SORM</span>
        </div>
        <Button size="sm" onClick={loadSorm} disabled={sormLoading} className="h-8 bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs px-4" data-testid="reliability-sorm-btn">
          {sormLoading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Gauge className="w-3 h-3 mr-1" />} Run SORM
        </Button>
      </div>

      {/* Portfolio SORM + PARS Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>PARS Score</span>
          <span className={`font-mono-data text-2xl font-bold ${pars?.pars >= 80 ? "text-emerald-500" : pars?.pars >= 50 ? "text-yellow-500" : "text-red-500"}`} data-testid="pars-score">{pars?.pars ?? "—"}</span>
        </div>
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Floor Reliability</span>
          <span className="font-mono-data text-2xl font-bold" style={{ color: gaugeColor }}>{sormData ? `${reliabilityPct.toFixed(0)}%` : "—"}</span>
        </div>
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Breach Prob</span>
          <span className="font-mono-data text-2xl font-bold" style={{ color: gaugeColor }}>{sormData ? `${breachPct.toFixed(1)}%` : "—"}</span>
        </div>
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>P(Any Assignment)</span>
          <span className="font-mono-data text-2xl font-bold">{((pars?.prob_any_assignment || 0) * 100).toFixed(1)}%</span>
        </div>
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Expected Margin Impact</span>
          <span className="font-mono-data text-2xl font-bold">${pars?.expected_margin_impact?.toLocaleString() || "0"}</span>
        </div>
      </div>

      {/* Sobol Risk Drivers (if SORM loaded) */}
      {sobol.length > 0 && (
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-xs font-bold mb-2 block" style={{ fontFamily: "Chivo, sans-serif" }}>Portfolio Risk Drivers (Sobol)</span>
          <div className="flex gap-3">
            {sobol.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px]">
                <span className="font-mono-data font-medium">{s.variable}</span>
                <div className="w-20 h-2 rounded-full overflow-hidden" style={{ background: "var(--hoip-bg)" }}>
                  <div className="h-full rounded-full" style={{ width: `${(s.total_order * 100)}%`, background: i === 0 ? "#EF4444" : i === 1 ? "#F59E0B" : "#3B82F6" }} />
                </div>
                <span className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{(s.total_order * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trade Regime Health Monitor */}
      <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <div className="p-4 border-b" style={{ borderColor: "var(--hoip-border)" }}>
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-blue-400" />
            <h2 className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Trade Regime Monitor</h2>
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(59,130,246,0.1)", color: "#3B82F6" }}>HMM Health + Exit Signals</span>
          </div>
          <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>Click any trade to view regime health, entry-vs-current regime drift, and exit signals.</p>
        </div>

        <table className="w-full dense-table" data-testid="reliability-trades-table">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strike</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>DTE</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Regime</th>
              <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Health</th>
              <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Exit</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Status</th>
              <th className="text-center text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t, i) => {
              const tr = tradeRegimes[t.id];
              const h = tr?.health;
              const ex = tr?.exit_signal;
              const rg = tr?.regime;
              const alertCol = { GREEN: "#10B981", YELLOW: "#F59E0B", RED: "#EF4444" };
              const isOpt = t.strategy && t.strategy !== "long_stock";
              const strikeStr = isOpt && t.short_strike ? (t.long_strike ? `$${t.short_strike}/$${t.long_strike}` : `$${t.short_strike}`) : "—";
              const dteStr = isOpt && t.expiry ? `${Math.max(0, Math.round((new Date(t.expiry) - new Date()) / 86400000))}d` : "—";
              return (
              <React.Fragment key={`trade-${t.id || i}`}>
                <tr className={`border-b cursor-pointer hover:bg-[#1A1A1A] ${expandedTrade === t.id ? "bg-[#1A1A1A]" : ""}`}
                  style={{ borderColor: "var(--hoip-divider)" }}
                  onClick={() => setExpandedTrade(expandedTrade === t.id ? null : t.id)}
                  data-testid={`reliability-trade-${t.ticker}`}>
                  <td className="font-mono-data text-xs font-medium">{t.ticker}</td>
                  <td className="text-xs capitalize" style={{ color: "var(--hoip-text-secondary)" }}>{t.strategy?.replace(/_/g, " ")}</td>
                  <td className="text-right font-mono-data text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{strikeStr}</td>
                  <td className="text-right font-mono-data text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{dteStr}</td>
                  <td>{rg ? <span className="text-[9px] px-1 py-0.5 rounded-sm font-bold" style={{ color: stateColors[rg.state], background: `${stateColors[rg.state]}15` }}>{rg.state?.split(" ")[0]} {(rg.prob * 100).toFixed(0)}%</span> : <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>loading...</span>}</td>
                  <td className="text-center">{h ? <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data" style={{ color: alertCol[h.alert_level], background: `${alertCol[h.alert_level]}15` }}>{h.alert_level} {h.health_score}</span> : <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}</td>
                  <td className="text-center">{ex ? <span className="text-[9px] px-1 py-0.5 rounded-sm font-bold" style={{ color: ex.exit_type === "HARD" ? "#EF4444" : "#F59E0B", background: ex.exit_type === "HARD" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)" }}>{ex.exit_type}</span> : <span className="text-[9px] text-emerald-500">—</span>}</td>
                  <td><span className={`text-xs px-2 py-0.5 border font-mono-data ${t.status === "open" ? "text-blue-400 border-blue-800" : t.status === "closed" ? "text-emerald-500 border-emerald-800" : "text-yellow-500 border-yellow-800"}`}>{t.status}</span></td>
                  <td className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Brain className="w-3 h-3" style={{ color: expandedTrade === t.id ? "#3B82F6" : "var(--hoip-text-tertiary)" }} />
                      {expandedTrade === t.id ? <ChevronUp className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} /> : <ChevronDown className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />}
                    </div>
                  </td>
                </tr>
                {expandedTrade === t.id && (
                  <tr><td colSpan={9} className="p-0 border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                    <TradeRegimePanel tradeId={t.id} ticker={t.ticker} strategy={t.strategy} cachedData={tradeRegimes[t.id]} />
                  </td></tr>
                )}
              </React.Fragment>
              );
            })}
            {trades.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
                No trades logged yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Position Assignment Probabilities */}
      {positions.length > 0 && (
        <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <div className="p-4 border-b" style={{ borderColor: "var(--hoip-border)" }}>
            <h2 className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Position Assignment Probabilities</h2>
          </div>
          <table className="w-full dense-table" data-testid="reliability-positions-table">
            <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strike</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>DTE</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>P(Assign)</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>P(Touch)</th>
              <th className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>POP</th>
              <th className="text-left text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Risk</th>
            </tr></thead>
            <tbody>
              {positions.map((p, i) => {
                const isMultiLeg = p.long_strike && p.short_strike && p.long_strike !== p.short_strike;
                const strikeLabel = isMultiLeg ? `$${p.short_strike}/$${p.long_strike}` : `$${p.short_strike}`;
                const assignLabel = p.is_long ? "P(ITM)" : "P(Assign)";
                return (
                <tr key={i} className="border-b hover:bg-[#1A1A1A]" style={{ borderColor: "var(--hoip-divider)" }}>
                  <td className="font-mono-data text-xs font-medium">{p.ticker}</td>
                  <td className="text-xs capitalize" style={{ color: "var(--hoip-text-secondary)" }}>{p.strategy_type?.replace(/_/g, " ")}</td>
                  <td className="text-right font-mono-data text-xs">{strikeLabel}</td>
                  <td className="text-right font-mono-data text-xs">{p.dte}d</td>
                  <td className="text-right font-mono-data text-xs">
                    <span>{((p.bs_assignment_prob || 0) * 100).toFixed(1)}%</span>
                    {p.is_long && <span className="text-[8px] ml-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>ITM</span>}
                  </td>
                  <td className="text-right font-mono-data text-xs" style={{ color: p.p_touch > 50 ? "#EF4444" : p.p_touch > 25 ? "#F59E0B" : p.p_touch != null ? "#10B981" : "var(--hoip-text-tertiary)" }}>
                    {p.p_touch != null ? (
                      <span>{p.p_touch}%{p.is_short_leg && isMultiLeg ? <span className="text-[8px] ml-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>short</span> : ""}</span>
                    ) : (
                      <span style={{ color: "var(--hoip-text-tertiary)" }}>IV unavailable</span>
                    )}
                  </td>
                  <td className="text-right font-mono-data text-xs text-emerald-500">{((p.probability_of_profit || 0) * 100).toFixed(1)}%</td>
                  <td><span className={`text-xs px-2 py-0.5 border font-mono-data ${p.risk_level === "LOW" ? "text-emerald-500 border-emerald-800" : p.risk_level === "MODERATE" ? "text-yellow-500 border-yellow-800" : "text-red-500 border-red-800"}`}>{p.risk_level}</span></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
